import { TourModel } from 'api-hooks/tour/tour-list/tour-detail';
import { useForm } from 'react-hook-form';
import * as Yup from 'yup';

export const TourPriceFormSchema = () =>
  Yup.object({
    id: Yup.string(),
    refId: Yup.string(),
    type: Yup.string().default(''),
    price: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .default(0),
    isDefault: Yup.boolean().default(false),

    //for memoize
    customId: Yup.string(),
  });

export const TourOtherPriceFormSchema = () =>
  Yup.object({
    id: Yup.string(),
    refId: Yup.string(),
    price: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .default(0),
    description: Yup.string().default(''),

    //for memoize
    customId: Yup.string(),
  });

export const TourFlightAirlineFormSchema = () =>
  Yup.object({
    id: Yup.string(),
    airlineId: Yup.string().default(''),
    preferenceId: Yup.string().default(''),
    date: Yup.date().default(new Date()),
    detail: Yup.string().default(''),
    route: Yup.string().default(''),
    time: Yup.string().default(''),

    //for memoize
    customId: Yup.string(),
  });

export const TourFlightFormSchema = () =>
  Yup.object({
    id: Yup.string(),
    refId: Yup.string(),
    name: Yup.string().default(''),
    note: Yup.string().default(''),
    tourFlightAirlines: Yup.array(TourFlightAirlineFormSchema()).default([]),

    //for memoize
    customId: Yup.string(),
  });

export const TourHotelDetailFormSchema = () =>
  Yup.object({
    id: Yup.string(),
    checkIn: Yup.date().default(new Date()),
    checkOut: Yup.date().default(new Date()),
    night: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .default(1),
    hotelRoomId: Yup.string().default(''),
    cityTax: Yup.string().default(''),
    description: Yup.string().default(''),

    //for memoize
    customId: Yup.string(),
  });

export const TourHotelFormSchema = () =>
  Yup.object({
    id: Yup.string(),
    refId: Yup.string(),
    hotelId: Yup.string().default(''),
    note: Yup.string().default(''),
    details: Yup.array(TourHotelDetailFormSchema()).default([]),

    //for memoize
    customId: Yup.string(),
  });

export const TourProductFormSchema = () =>
  Yup.object({
    id: Yup.string(),
    refId: Yup.string(),
    productVariantId: Yup.object({
      id: Yup.string().default(''),
      label: Yup.string().default(''),
    }),
    qty: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .default(0),
    description: Yup.string().default(''),

    //for memoize
    customId: Yup.string(),
    //just information
    price: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .default(0),
    stock: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .default(0),
    avgCogs: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .default(0),
  });

export const TourListFormSchema = () =>
  Yup.object({
    refId: Yup.string(),
    name: Yup.string().default(''),
    code: Yup.string().default(''),
    needApproval: Yup.boolean().default(false),
    description: Yup.string().default(''),
    startedAt: Yup.date().default(new Date()),
    endedAt: Yup.date().default(new Date()),
    day: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .default(1),
    tourCategoryId: Yup.string().default(''),
    employeeId: Yup.string().default(''),
    operatorId: Yup.string().default(''),
    salesmanId: Yup.string().default(''),
    adminCommission: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .default(0),
    operatorCommission: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .default(0),
    salesCommission: Yup.number()
      .transform((value) => (isNaN(value) ? undefined : value))
      .default(0),
    type: Yup.string().default('private'),
    tourFlights: Yup.array(TourFlightFormSchema()).default([]),
    tourHotels: Yup.array(TourHotelFormSchema()).default([]),
    tourProducts: Yup.array(TourProductFormSchema()).default([]),
    tourPrices: Yup.array(TourPriceFormSchema()).default([]),
    tourOtherPrices: Yup.array(TourOtherPriceFormSchema()).default([]),

    transactionAt: Yup.date().default(new Date()),
    //for acquire needApproval
    tempTransactionAt: Yup.date().default(new Date()),
  });

export type TourListFormType = Yup.InferType<
  ReturnType<typeof TourListFormSchema>
> & { tourList?: TourModel };

export type TourFlightFormType = Yup.InferType<
  ReturnType<typeof TourFlightFormSchema>
>;

export type TourFlightAirlineFormType = Yup.InferType<
  ReturnType<typeof TourFlightAirlineFormSchema>
>;

export type TourHotelFormType = Yup.InferType<
  ReturnType<typeof TourHotelFormSchema>
>;

export type TourHotelDetailFormType = Yup.InferType<
  ReturnType<typeof TourHotelDetailFormSchema>
>;

export type TourPriceFormType = Yup.InferType<
  ReturnType<typeof TourPriceFormSchema>
>;

export type TourOtherPriceFormType = Yup.InferType<
  ReturnType<typeof TourOtherPriceFormSchema>
>;

export type TourProductFormType = Yup.InferType<
  ReturnType<typeof TourProductFormSchema>
>;

export type TourListFormMethod = ReturnType<typeof useForm<TourListFormType>>;
